import { useRef } from "react";
import dayjs from "dayjs";
import { Form, DatePicker, Input, Select, Switch, Button, Space } from "antd";

import { Stack } from "@components/Page";
import { Bold } from "@components/Text";

import BeneficiariesTable, { type BeneficiariesTableHandle } from "../BeneficiariesTable";
import type { Investment, Frequency } from "../types";
import getNextDate from "./helpers/getNextDate";

type ScheduleFormValues = {
  projectId: string;
  name: string;
  startDate: string;
  distributionFrequency: Frequency;
  shouldRoundCents: boolean;
}

const LABEL_PROJECT = "Project";
const LABEL_START_DATE = "Start Date";
const LABEL_FREQUENCY = "Frequency";
const LABEL_NEXT_DATE = "Next Distribution";
const LABEL_ROUND_CENTS = "Round Cents";
const LABEL_SUBMIT = "Publish Schedule";

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  // { value: "DAILY",         label: "Daily" },
  // { value: "WEEKLY",        label: "Weekly" },
  { value: "MONTHLY",       label: "Monthly" },
  { value: "QUARTERLY",     label: "Quarterly" },
  { value: "SEMI_ANNUALLY", label: "Semi-Annually" },
  { value: "ANNUALLY",      label: "Annually" },
];

const startDateItemProps = {
  name: "startDate",
  label: LABEL_START_DATE,
  rules: [{ required: true, message: `${LABEL_START_DATE} is required` }],
  normalize: (value: dayjs.Dayjs | null) => value ? value.toISOString() : value,
  getValueProps: (value: string | null) => ({ value: value ? dayjs(value) : value }),
};

const frequencyItemProps = {
  name: "distributionFrequency",
  label: LABEL_FREQUENCY,
  rules: [{ required: true, message: `${LABEL_FREQUENCY} is required` }],
};

const datePickerProps = {
  picker: "date" as const,
  format: "MM/DD/YYYY",
  placeholder: LABEL_START_DATE,
  allowClear: false,
};

const projectIdItemProps = {
  name: "projectId",
  hidden: true,
};

const nameItemProps = {
  name: "name",
  hidden: true,
};

const roundCentsItemProps = {
  name: "shouldRoundCents",
  label: LABEL_ROUND_CENTS,
  valuePropName: "checked",
};

const submitItemProps = {
  style: { margin: 0 },
};

const selectProps = {
  style: { width: 140 },
  options: FREQUENCY_OPTIONS,
  placeholder: LABEL_FREQUENCY,
};

type Props = {
  projectId: string;
  projectName: string;
  investments: Investment[];
}


const DistributionsScheduleForm = ({ projectId, projectName, investments }: Props) => {
  const [ form ] = Form.useForm<ScheduleFormValues>();
  const beneficiariesTableRef = useRef<BeneficiariesTableHandle>(null);

  const startDate = Form.useWatch("startDate", form);
  const frequency = Form.useWatch("distributionFrequency", form);
  const shouldRoundCents = Form.useWatch("shouldRoundCents", form);

  const nextDate = getNextDate(startDate, frequency);

  const onFinish = (payload: ScheduleFormValues) => {
    const totalAmount = beneficiariesTableRef.current?.getTotalAmount();
    const selectedDistributions = beneficiariesTableRef.current?.getSelectedDistributions();

    const distributions = selectedDistributions
      ?.map(({ investmentId, amount, investorAccountId, returnRate, tierId, investedAmount }) => ({
        investmentId,
        amount,
        tierId,
        returnRate,
        investorAccountId,
        investedAmount,
      }));

    // eslint-disable-next-line no-console
    console.log('payload', { ...payload, totalAmount, distributions });
  };

  const initialValues: ScheduleFormValues = {
    name: projectName,
    startDate: dayjs().toISOString(),
    shouldRoundCents: false,
    distributionFrequency: "MONTHLY",
    projectId,
  };

  const formProps = {
    layout: "inline" as const,
    initialValues,
    form,
    onFinish,
  };

  return (
    <Stack>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Space size={16}>
          <span>
            {`${LABEL_PROJECT}:`} <Bold>{projectName}</Bold>
          </span>
          {
            nextDate && (
              <span>
                {`${LABEL_NEXT_DATE}:`} <Bold>{nextDate}</Bold>
              </span>
            )
          }
        </Space>

        <Form {...formProps}>
          <Form.Item {...projectIdItemProps}>
            <Input />
          </Form.Item>

          <Form.Item {...nameItemProps}>
            <Input />
          </Form.Item>

          <Form.Item {...frequencyItemProps}>
            <Select {...selectProps} />
          </Form.Item>

          <Form.Item {...startDateItemProps}>
            <DatePicker {...datePickerProps} />
          </Form.Item>

          <Form.Item {...roundCentsItemProps}>
            <Switch />
          </Form.Item>

          <Form.Item {...submitItemProps}>
            <Button type="primary" htmlType="submit">{LABEL_SUBMIT}</Button>
          </Form.Item>
        </Form>
      </div>

      <BeneficiariesTable
        ref={beneficiariesTableRef}
        investments={investments}
        distributionFrequency={frequency}
        shouldRoundCents={shouldRoundCents}
      />
    </Stack>
  );
};

export default DistributionsScheduleForm;
